import { useEffect } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Shield, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQueryClient } from "@tanstack/react-query";

export default function AuthSuccess() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Check for error in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    
    if (error) {
      toast({
        title: "Authentication Error",
        description: error === 'invalid_state' 
          ? "Invalid authentication state. Please try again."
          : error === 'oauth_failed'
          ? "OAuth authentication failed. Please try again."
          : `Authentication failed: ${error}`,
        variant: "destructive",
      });
      setTimeout(() => {
        setLocation("/signin");
      }, 2000);
      return;
    }
    
    // Session is already set by the backend, just verify and redirect
    const verifySession = async () => {
      try {
        // Verify session by fetching user data
        const response = await fetch("/api/user");
        if (response.ok) {
          const user = await response.json();
          
          // Invalidate queries to refresh user data
          queryClient.invalidateQueries({ queryKey: ["/api/user"] });
          
          toast({
            title: "Welcome to PCI Scan Report!",
            description: "Successfully signed in!",
          });
          
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            setLocation("/");
          }, 1500);
        } else {
          throw new Error("Session verification failed");
        }
      } catch (error) {
        console.error("Error verifying session:", error);
        toast({
          title: "Authentication Error",
          description: "Failed to verify session. Please try signing in again.",
          variant: "destructive",
        });
        setTimeout(() => {
          setLocation("/signin");
        }, 2000);
      }
    };
    
    verifySession();
  }, [setLocation, toast, queryClient]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 bg-purple-500 rounded-xl shadow-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-semibold text-slate-900 dark:text-white">
              Authentication Successful
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-purple-500" />
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              You have been successfully authenticated. Redirecting to your dashboard...
            </p>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
              <div className="bg-purple-500 h-2 rounded-full animate-pulse w-full"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}